import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { Keyboard } from '@awesome-cordova-plugins/keyboard/ngx';
import { AlertController, ModalController, Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { FooziAiConciergeService } from '../foozi-ai-concierge.service';
import { PRACTICE_SCORE_TARGET, PracticeBotService } from '../practice-bot.service';
import { HomeUiService } from '../home-ui.service';
import { I18n } from '../../i18n.service';
import { AppState } from '../../app.service';
import { ToasterService } from '../../toaster.service';
import { PracticeBotModalComponent } from '../practice-bot-modal/practice-bot-modal.component';
import {
  MicroActionHistoryHandler,
  MicroActionHistoryModalComponent,
} from '../micro-action-history-modal/micro-action-history-modal.component';
import { historyDateLabel } from './history-date';
import { environment } from 'src/environments/environment';

export type FooziHistoryTab = 'chats' | 'microActions' | 'practice';

const HISTORY_TABS: FooziHistoryTab[] = ['chats', 'microActions', 'practice'];

/**
 * Which tab was last open, for the rest of this app run. Re-opening History from the header should
 * return the learner to the section they were in, not to Chats — the same reason coming back from a
 * child modal does.
 */
const HISTORY_TAB_KEY = 'foozi_history_tab';

/**
 * How far past the first page to look for an action the caller asked to open.
 *
 * Bounded: a row that is not there at all — deleted, or from a cycle the learner has since lost
 * access to — must not walk the whole list looking for it.
 */
const REQUESTED_ACTION_CHASES = 5;

/**
 * One page. All three endpoints page server-side on limit/offset now, so this is the page size sent
 * to them rather than a slice taken here.
 */
export const HISTORY_PAGE_SIZE = 20;

/** How long a summarised conversation title may run before it is cut at a word. */
const TITLE_MAX_CHARS = 48;

/**
 * The longest name PATCH /session/{id} accepts — past it the server answers 422 ("title must be at
 * most 200 characters"). The rename dialog counts against it as the learner types, so the refusal
 * is seen before Save rather than after it.
 */
const RENAME_TITLE_MAX_CHARS = 200;
/** Space kept between the rename dialog and the keyboard, and the top of the screen. */
const RENAME_KEYBOARD_GAP = 16;

/**
 * Where the learner's own names for conversations live. localStorage, not sessionStorage: a name
 * they chose should outlast the tab. Per-device, because the API has no title field to sync it to.
 */
const TITLE_STORAGE_KEY = 'foozi_chat_titles';

/**
 * What the modal hands back when a chat is opened — the one row type whose destination is the panel
 * behind this modal, so it closes and the caller takes over. Micro-actions and practice open their
 * own modal from here instead, on top of this one.
 */
/**
 * Dismiss role for "the conversation already on screen was tapped".
 *
 * Distinct from an ordinary close, which has the panel re-read the conversation in case something
 * opened from here wrote to it. Tapping the row you are already reading changes nothing, so it
 * should cost nothing: the modal closes and no request is made.
 */
export const HISTORY_DISMISS_CURRENT = 'current-chat';

export interface FooziHistorySelection {
  tab: FooziHistoryTab;
  /** The session to load into the panel. */
  row: any;
}

@Component({
  selector: 'app-foozi-history-modal',
  templateUrl: './foozi-history-modal.component.html',
  styleUrls: ['./foozi-history-modal.component.scss'],
})
export class FooziHistoryModalComponent implements OnInit, OnDestroy {
  tab: FooziHistoryTab = 'chats';

  chats: any[] = [];
  microActions: any[] = [];
  practice: any[] = [];

  /** Per tab, so switching tabs never shows the previous tab's spinner or error. */
  loading: Record<FooziHistoryTab, boolean> = { chats: false, microActions: false, practice: false };
  loaded: Record<FooziHistoryTab, boolean> = { chats: false, microActions: false, practice: false };
  failed: Record<FooziHistoryTab, boolean> = { chats: false, microActions: false, practice: false };
  /** False once a page came back short, which is the only reliable end-of-list signal here. */
  hasMore: Record<FooziHistoryTab, boolean> = { chats: true, microActions: true, practice: true };
  /** Whether a tab has fetched its first page, so a re-open pages on rather than starting over. */
  private started: Record<FooziHistoryTab, boolean> = { chats: false, microActions: false, practice: false };
  /**
   * How many rows have been FETCHED per tab, which is not how many are shown: empty conversations
   * are dropped. The next page's offset has to count what the server sent.
   */
  private fetched: Record<FooziHistoryTab, number> = { chats: 0, microActions: 0, practice: 0 };

  /**
   * A micro-action to open as soon as its row is loaded.
   *
   * For the return leg of a cycle switch: the learner asked to open this action, the app reloaded
   * around them, and this puts them back in front of it — with History behind it, so closing it
   * leaves them where they were rather than on a home screen they never asked for.
   */
  @Input() openActionId: number | string | null = null;

  /**
   * The micro-action chips whose flow lives on whatever opened this modal (calendar commit, document
   * open, completion). Passed straight through to the micro-action modal this one presents.
   */
  @Input() onCommit: MicroActionHistoryHandler;
  @Input() onOpenTool: MicroActionHistoryHandler;
  @Input() onMarkCompleted: MicroActionHistoryHandler;

  /**
   * The conversation the panel behind this modal is showing, so the list can say which one that is.
   *
   * Kept as a field rather than read per row: the getter runs for every row on every change
   * detection pass, and sessionStorage is only the fallback for a run where nothing published it.
   */
  activeSessionId: string | null = null;

  /** Pages openRequestedAction may fetch looking for its row before it gives up. */
  private requestedActionChases = REQUESTED_ACTION_CHASES;

  isMobile;

  private subscriptions = new Subscription();

  constructor(
    private modalController: ModalController,
    private alertController: AlertController,
    private fooziService: FooziAiConciergeService,
    private practiceBotService: PracticeBotService,
    private cdRef: ChangeDetectorRef,
    private appState: AppState,
    private toaster: ToasterService,
    public homeUiService: HomeUiService,
    public platform: Platform,
    public i18n: I18n,
    @Optional() private keyboard?: Keyboard,
  ) {}

  ngOnInit() {
    // The action decides the tab: landing on Chats and making them find it again would undo the
    // point of coming back here. Held here rather than written to storage and read back below —
    // the first render can select a tab of its own in between, and then this would be reading its
    // choice instead of making one.
    const wanted: FooziHistoryTab = this.openActionId != null ? 'microActions' : this.rememberedTab();
    this.isMobile = this.appState.get('isMobile');
    // A BehaviorSubject, so this arrives before the first page does. The stored id stands in where
    // nothing published one — a run that resumed a conversation without passing through the panel's
    // own adopt or open path.
    this.subscriptions.add(this.fooziService.activeSession$.subscribe((sessionId) => {
      this.activeSessionId = sessionId || sessionStorage.getItem('foozi_session') || null;
      this.cdRef.detectChanges();
    }));
    this.selectTab(wanted);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /** Tabs load on first open only — coming back to one already fetched must not re-request. */
  selectTab(tab: FooziHistoryTab) {
    if (!HISTORY_TABS.includes(tab)) { return; }
    this.tab = tab;
    this.rememberTab(tab);
    if (this.loaded[tab] || this.loading[tab]) { return; }
    this.load(tab);
  }

  /** The tab this run left off on, or Chats the first time. */
  private rememberedTab(): FooziHistoryTab {
    try {
      const stored = sessionStorage.getItem(HISTORY_TAB_KEY) as FooziHistoryTab;
      return HISTORY_TABS.includes(stored) ? stored : 'chats';
    } catch {
      // Private-mode storage failures must not keep History from opening at all.
      return 'chats';
    }
  }

  private rememberTab(tab: FooziHistoryTab): void {
    try {
      sessionStorage.setItem(HISTORY_TAB_KEY, tab);
    } catch {
      // Not being able to remember the tab is not worth failing the interaction over.
    }
  }

  /** Re-fetch the current tab from the top, from the "Try again" the failure state offers. */
  retry() {
    this.refreshTab(this.tab);
  }

  /**
   * Throw away a tab's pages and fetch the first one again.
   *
   * Used when returning from a modal opened here: customising an action, committing it, completing
   * it or finishing a practice session all change the very rows this list is showing, and the
   * learner should not have to close History to see it. Deep paging is rebuilt by scrolling again —
   * a stale first page is the worse trade.
   */
  private refreshTab(tab: FooziHistoryTab, quiet = false) {
    // What the rows on screen are worth as an offset, in case this refresh does not replace them.
    const fetchedBefore = this.fetched[tab];
    const hadMore = this.hasMore[tab];
    this.hasMore[tab] = true;
    this.fetched[tab] = 0;           // back to the first page: what changed may have reordered it
    if (quiet) {
      // Coming back from a child modal, where the learner did not ask for anything to happen. The
      // rows they were reading stay on screen and are swapped for the new ones when those arrive —
      // usually identical, and clearing first made the list blink out and return a second later.
      this.load(tab, (failed) => {
        // A quiet refresh that fails leaves those rows up, and an offset of zero no longer
        // describes them: the next page request would fetch page one again and append it beneath
        // rows already on screen. Put the counters back where the visible list left them.
        if (!failed) { return; }
        this.fetched[tab] = fetchedBefore;
        this.hasMore[tab] = hadMore;
      }, []);
      return;
    }
    this.loaded[tab] = false;
    this.rowsFor(tab).length = 0;
    this.load(tab);
  }

  /**
   * The next page, from ion-infinite-scroll. Completing the event is what re-arms the loader, so it
   * happens on every outcome — including a failure, which would otherwise leave it spinning.
   */
  loadMore(event: any) {
    const tab = this.tab;
    if (this.loading[tab] || !this.hasMore[tab]) {
      event?.target?.complete();
      return;
    }
    this.load(tab, () => event?.target?.complete());
  }

  /**
   * A tab's next page.
   *
   * `staged` makes it a quiet refresh: rows are collected there instead of being appended, and the
   * list on screen is swapped for them once the walk is over. Nothing is cleared in the meantime, so
   * a refresh nobody asked for costs the reader nothing — not the rows, not the scroll position, and
   * not a failure state if the request does not come back.
   *
   * `done` is told whether the page came back, because a caller that kept the old rows has paging
   * counters to put back.
   */
  private load(tab: FooziHistoryTab, done?: (failed?: boolean) => void, staged?: any[]) {
    this.loading[tab] = true;
    if (!staged) { this.failed[tab] = false; }
    // Offset by what has been FETCHED, which for chats is not what is shown: empty conversations
    // are dropped from the page. Offsetting by the rows on screen would re-request the ones skipped.
    const offset = this.fetched[tab];

    const request =
      tab === 'chats' ? this.fooziService.listSessions(HISTORY_PAGE_SIZE, offset)
      : tab === 'microActions' ? this.fooziService.listCustomizations(undefined, HISTORY_PAGE_SIZE, offset)
      // action_offset walks the cards; each card brings its own first page of attempts.
      : this.practiceBotService.getPracticeHistory(undefined, {
          actionLimit: HISTORY_PAGE_SIZE,
          actionOffset: offset,
        });

    this.subscriptions.add(request.subscribe(
      (response: any) => {
        const page = this.pageFromResponse(tab, response);
        const rows = page.filter((row: any) => this.isWorthListing(tab, row));

        this.fetched[tab] += page.length;
        if (staged) {
          staged.push(...rows);
        } else {
          this.rowsFor(tab).push(...rows);
          this.orderByCycle(tab);
        }
        this.hasMore[tab] = this.hasMoreFromResponse(tab, response, page.length);
        this.loading[tab] = false;
        this.loaded[tab] = true;
        this.started[tab] = true;
        this.cdRef.detectChanges();

        // A whole page can filter away — every conversation empty, or every action still without a
        // completed attempt. That would show "Nothing here yet" with no loader to arm, since the
        // infinite scroll needs a row on screen before it can ask for more. Keep going; this ends
        // when the server says there is no more.
        //
        // Only while the offset is moving. It advances by page.length, so an empty page leaves it
        // exactly where it was and the next call repeats this same request — for as long as the
        // server keeps has_more set, which is to say without end, with the loader never completed.
        if (!rows.length && this.hasMore[tab] && page.length) {
          this.load(tab, done, staged);
          return;
        }
        // Nothing at all came back, yet the server claims another page. There is no request that
        // could reach it: the offset cannot step past a page of zero. Treat it as the end of the
        // list rather than leaving the tab holding a next page it can never fetch — "Try again"
        // and re-opening the tab both start over from the first page.
        if (!page.length && this.hasMore[tab]) {
          console.log('[history] empty page with has_more set — treating as the end of', tab);
          this.hasMore[tab] = false;
        }
        // The walk is over: the list becomes what this refresh found, in one step. A row deleted
        // inside the child modal goes here, and one added appears, without the list ever emptying.
        if (staged) {
          const list = this.rowsFor(tab);
          list.length = 0;
          list.push(...staged);
          this.orderByCycle(tab);
          this.cdRef.detectChanges();
        }
        this.openRequestedAction(tab, staged);
        done?.();
      },
      () => {
        // Keep the tab usable: what has loaded stays, with a retry, rather than a spinner that
        // never stops. A failed page must not be treated as the end of the list.
        this.loading[tab] = false;
        this.loaded[tab] = true;
        // A quiet refresh failing is not the reader's problem: they are looking at rows that are
        // still perfectly good, and a "Try again" over them would be about a request they never made.
        if (!staged) { this.failed[tab] = true; }
        this.cdRef.detectChanges();
        done?.(true);
      },
    ));
  }

  /** One page of rows out of a tab's envelope. */
  private pageFromResponse(tab: FooziHistoryTab, response: any): any[] {
    if (tab === 'chats') { return response?.sessions ?? []; }
    if (tab === 'microActions') { return this.customizationsFrom(response); }
    return this.practiceBotService.practiceActionsFrom(response) ?? [];
  }

  /**
   * Whether another page exists. /sessions and /practice say so outright; /customizations does not,
   * so a short page is taken as the end — the same rule, just inferred.
   */
  private hasMoreFromResponse(tab: FooziHistoryTab, response: any, pageLength: number): boolean {
    if (tab === 'microActions') { return pageLength >= HISTORY_PAGE_SIZE; }
    return !!response?.has_more;
  }

  /**
   * Whether a row is worth listing.
   *
   * Chats: a conversation with no messages has nothing to preview, nothing to reopen, and no title
   * worth inventing for it. Practice: an action whose every attempt was abandoned or is still being
   * analyzed has no feedback to show, so the row would open onto nothing.
   *
   * Micro-actions are always listed — a customisation exists by virtue of having been made.
   */
  private isWorthListing(tab: FooziHistoryTab, row: any): boolean {
    if (tab === 'practice') { return (row?.completedCount ?? 0) > 0; }
    if (tab !== 'chats') { return true; }

    const count = row?.message_count;
    if (typeof count === 'number') { return count > 0; }
    // No count from the server: fall back to whether there is anything to show for it.
    return !!this.chatPreview(row);
  }

  /** /v2/customizations answers with a bare array; the keyed shapes are tolerated as a fallback. */
  private customizationsFrom(response: any): any[] {
    const list = Array.isArray(response) ? response : (response?.customizations ?? response?.items ?? []);
    return Array.isArray(list) ? list : [];
  }

  /**
   * Whatever the server gives us to describe a conversation by.
   *
   * `title` is deliberately not read here — it is the name the learner gave the conversation, and
   * a row with a name but no messages still has nothing to preview.
   */
  private chatPreview(session: any): string {
    return (session?.preview || session?.first_message || '').trim();
  }

  /**
   * A conversation's title: the learner's own name for it if they have set one, otherwise a short
   * one summarised from its opening message.
   *
   * The server holds the name now, so it follows the learner to their other devices. Names set
   * before it did are still read from this device, and are written back to the server the next
   * time that conversation is renamed. A row the server gave us nothing to describe shows no title
   * at all rather than an invented one; its count and date are at least true.
   */
  chatTitle(session: any): string {
    const named = (session?.title || '').trim();
    if (named) { return named; }
    const sessionId = session?.session_id;
    const own = sessionId ? this.storedTitles()[sessionId] : '';
    if (own) { return own; }
    return this.summarise(this.chatPreview(session));
  }

  /** Whether this row's title is the learner's own rather than one derived from the first message. */
  isRenamed(session: any): boolean {
    const sessionId = session?.session_id;
    return !!((session?.title || '').trim() || (sessionId && this.storedTitles()[sessionId]));
  }

  /**
   * A first message shortened to a title. Cut at a word so it does not end mid-syllable, and only
   * when it is long enough to be worth cutting.
   */
  private summarise(text: string): string {
    const clean = (text || '').replace(/\s+/g, ' ').trim();
    if (clean.length <= TITLE_MAX_CHARS) { return clean; }
    const cut = clean.slice(0, TITLE_MAX_CHARS);
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > TITLE_MAX_CHARS / 2 ? cut.slice(0, lastSpace) : cut).trim()}…`;
  }

  /** The pencil on a chat row: lets the learner name a conversation themselves. */
  async renameChat(row: any, event: Event) {
    event.stopPropagation();
    const sessionId = row?.session_id;
    if (!sessionId) { return; }

    // The dialog stays open until the server has answered: a name is never shown as saved before it
    // is, and while one rename is saving no other can start (Save, Cancel, the backdrop and back are
    // all held), so two renames of one conversation cannot race.
    let saving = false;
    let alert: HTMLIonAlertElement;
    alert = await this.alertController.create({
      header: this.i18n.t('Rename conversation'),
      cssClass: 'rename-chat-alert',
      inputs: [{
        name: 'title',
        // A textarea so a long name wraps and can be read in full. No maxlength: a paste is never cut;
        // the live count (watchRenameField) flags a name over the server's limit and holds Save instead.
        type: 'textarea',
        value: this.editableChatTitle(row),
        placeholder: this.i18n.t('Conversation name'),
      }],
      buttons: [
        { text: this.i18n.t('Cancel'), role: 'cancel', cssClass: 'rename-chat-alert__cancel', handler: () => !saving },
        {
          text: this.i18n.t('Save'),
          cssClass: 'rename-chat-alert__save',
          handler: (data: any) => {
            if (saving) { return false; }
            // One line: Enter cannot put a line break into the name.
            const title = (data?.title || '').replace(/\s+/g, ' ').trim();
            // Save is disabled for both already; this covers a tap that lands before the input event.
            if (!title || title.length > RENAME_TITLE_MAX_CHARS) { return false; }
            saving = true;
            this.showRenameState(alert, { saving: true });
            // Whether the server answered. The request is cancelled with the history modal (it sits in
            // this.subscriptions), and then neither callback runs: without the finalize below, saving
            // would stay true and Cancel, the ✕ and the backdrop would all stay held — no way out.
            let answered = false;
            const request = this.fooziService.renameSession(sessionId, title).pipe(
              finalize(() => {
                if (answered) { return; }
                saving = false;
                alert.dismiss(null, 'cancel');
              }),
            ).subscribe(
              (updated: any) => {
                answered = true;
                // What the server made of it, where it answered with the field; else what was sent.
                const saved = updated && 'title' in updated ? (updated.title || '').trim() : title;
                row.title = saved || title;
                this.storeTitle(sessionId, row.title);
                this.cdRef.detectChanges();
                alert.dismiss();
              },
              (error: any) => {
                answered = true;
                saving = false;
                this.showRenameState(alert, { saving: false, error: this.renameErrorMessage(error) });
              },
            );
            this.subscriptions.add(request);
            return false;
          },
        },
      ],
    });
    // Listening before it opens: Ionic may focus the field as it does, raising the keyboard at once.
    this.keepRenameAboveKeyboard(alert);
    await alert.present();
    this.addRenameCloseButton(alert, () => saving);
    this.watchRenameField(alert);
  }

  /**
   * On a phone the keyboard covers the lower half of the screen without resizing the page (iOS), so
   * the centred dialog's Save and Cancel sat under it. While the keyboard is up, the dialog is laid out
   * at the bottom of the space left above the keyboard, its buttons 16px clear of it — or, when it is
   * too tall for that space, at the top of the screen — and goes back to the centre when the keyboard
   * goes. Where the page is resized for the keyboard instead, it already fits and nothing changes.
   *
   * It is placed through the alert's own layout (the full-screen ion-alert centres its wrapper with
   * flexbox), not by moving the wrapper: Ionic's open animation keeps its final transform on the
   * wrapper, which would override any transform set here.
   * Heights come from the keyboard plugin in the app, and from the visual viewport in a browser.
   */
  private keepRenameAboveKeyboard(alert: HTMLIonAlertElement): void {
    const host = alert as unknown as HTMLElement;
    if (!host?.style) { return; }
    const startHeight = window.innerHeight;
    const reset = () => {
      host.style.alignItems = '';
      host.style.paddingTop = '';
      host.style.paddingBottom = '';
    };
    const place = (visibleBottom: number) => {
      reset();
      const wrapper = host.querySelector?.<HTMLElement>('.alert-wrapper');
      if (!wrapper) { return; }
      const dialog = wrapper.getBoundingClientRect();
      if (dialog.bottom + RENAME_KEYBOARD_GAP <= visibleBottom) { return; }
      const screen = host.getBoundingClientRect();
      const room = visibleBottom - screen.top - 2 * RENAME_KEYBOARD_GAP;
      if (dialog.height <= room) {
        host.style.alignItems = 'flex-end';
        host.style.paddingBottom = `${screen.bottom - visibleBottom + RENAME_KEYBOARD_GAP}px`;
      } else {
        host.style.alignItems = 'flex-start';
        host.style.paddingTop = `${RENAME_KEYBOARD_GAP}px`;
      }
    };
    const subscriptions = new Subscription();
    let onViewportResize: (() => void) | null = null;
    if (this.keyboard && this.platform?.is?.('cordova')) {
      subscriptions.add(this.keyboard.onKeyboardWillShow().subscribe((info: any) => {
        const height = Number(info?.keyboardHeight) || 0;
        place(Math.min(window.innerHeight, startHeight - height));
      }));
      subscriptions.add(this.keyboard.onKeyboardWillHide().subscribe(() => reset()));
    } else if (window.visualViewport) {
      const viewport = window.visualViewport;
      onViewportResize = () => place(viewport.offsetTop + viewport.height);
      viewport.addEventListener('resize', onViewportResize);
    }
    alert.onDidDismiss?.()?.then(() => {
      subscriptions.unsubscribe();
      if (onViewportResize) { window.visualViewport?.removeEventListener('resize', onViewportResize); }
    });
  }

  /**
   * The ✕ at the top of the dialog. Ionic alerts have none, so it is added once the alert is on the
   * page; it closes like Cancel, and is held while a name is saving, as Cancel is.
   */
  private addRenameCloseButton(alert: HTMLIonAlertElement, isSaving: () => boolean): void {
    const wrapper = alert?.querySelector?.('.alert-wrapper');
    if (!wrapper || wrapper.querySelector('.rename-chat-alert__close')) { return; }
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'alert-button rename-chat-alert__close';
    close.setAttribute('aria-label', this.i18n.t('Close'));
    // Drawn inline rather than from the icon font, so the way out is there even before fonts load.
    close.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">'
      + '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
    close.addEventListener('click', () => {
      if (!isSaving()) { alert.dismiss(null, 'cancel'); }
    });
    wrapper.appendChild(close);
  }

  /**
   * What the rename dialog starts from: the learner's own name, or — for a chat never renamed — the
   * whole first message, not the 48-character summary the list shows.
   */
  private editableChatTitle(session: any): string {
    if (this.isRenamed(session)) { return this.chatTitle(session); }
    return this.chatPreview(session).replace(/\s+/g, ' ').trim();
  }

  /** How long a name is as it would be sent: one line, trimmed. What the server's limit applies to. */
  private renameTitleLength(value: string): number {
    return (value || '').replace(/\s+/g, ' ').trim().length;
  }

  /**
   * Counts the name as it is typed, against the server's limit, so a name too long to save is caught
   * here rather than refused after Save: a live "37/200" beside the error line, grey up to the
   * limit; past it the count turns red, the error line says so and Save is off. Nothing is cut — what was
   * typed or pasted stays, for the learner to trim. Save is also off while the name is blank, and
   * going back to the field (a tap or an edit) clears an error the server sent.
   */
  private watchRenameField(alert: HTMLIonAlertElement): void {
    const field = alert?.querySelector?.<HTMLTextAreaElement>('textarea.alert-input');
    const save = alert?.querySelector?.<HTMLButtonElement>('button.rename-chat-alert__save');
    const wrapper = alert?.querySelector?.('.alert-wrapper');
    if (!field || !save || !wrapper) { return; }

    // Ionic alerts have no counter, so it is added beside the error line once the alert is up.
    const count = document.createElement('span');
    count.className = 'rename-chat-alert__count';
    count.setAttribute('aria-hidden', 'true');   // per keystroke is too chatty; the over-limit line is announced
    wrapper.appendChild(count);

    const tooLongMessage = this.i18n.t('Too long — use %{max} characters or fewer.', { max: RENAME_TITLE_MAX_CHARS });
    let overLimit = false;

    const sync = () => {
      const length = this.renameTitleLength(field.value);
      count.textContent = `${length}/${RENAME_TITLE_MAX_CHARS}`;
      count.classList.toggle('rename-chat-alert__count--over', length > RENAME_TITLE_MAX_CHARS);

      const nowOver = length > RENAME_TITLE_MAX_CHARS;
      // Only on crossing the limit: the message re-renders the alert, which is not wanted per keystroke.
      if (nowOver !== overLimit) {
        overLimit = nowOver;
        this.showRenameState(alert, { saving: false, error: nowOver ? tooLongMessage : undefined });
      }
      save.disabled = field.readOnly || !length || nowOver;
    };

    const clearServerError = () => {
      if (!overLimit && alert.classList?.contains('rename-chat-alert--error')) {
        this.showRenameState(alert, { saving: false });
      }
    };
    field.addEventListener('focus', clearServerError);
    field.addEventListener('input', () => { clearServerError(); sync(); });
    sync();
  }

  /**
   * Saving: Save turns into a spinner, the field is read-only and nothing closes the dialog.
   * readOnly rather than disabled — Ionic re-renders the textarea's disabled and value from its own
   * state whenever a prop such as `message` changes; it never renders `disabled` on the buttons.
   */
  private showRenameState(alert: HTMLIonAlertElement, state: { saving: boolean; error?: string }) {
    const { saving, error } = state;
    alert.backdropDismiss = !saving;
    alert.message = error || undefined;
    alert.classList?.toggle('rename-chat-alert--saving', saving);
    alert.classList?.toggle('rename-chat-alert--error', !!error);
    const field = alert.querySelector?.<HTMLTextAreaElement>('textarea.alert-input');
    if (field) { field.readOnly = saving; }
    const length = this.renameTitleLength(field?.value || '');
    alert.querySelectorAll?.<HTMLButtonElement>('button.alert-button').forEach((b) => {
      b.disabled = saving
        || (b.classList.contains('rename-chat-alert__save') && (!length || length > RENAME_TITLE_MAX_CHARS));
    });
  }

  /**
   * A rename failure in words a learner can act on, short enough for the one line kept under the
   * field (so the dialog keeps its shape). The server's own text is never shown: a 422's
   * `detail` is a list of objects (which rendered as "[object Object]"), and is only read to tell
   * the cases apart. The list may arrive as `error.error.detail` or as the whole body.
   */
  renameErrorMessage(error: any): string {
    const body = error?.error;
    const detail = Array.isArray(body?.detail) ? body.detail : (Array.isArray(body) ? body : []);
    const tooLong = detail.find((d: any) => d?.type === 'string_too_long'
      || /at most \d+ characters?|too long/i.test(d?.msg || ''));
    if (tooLong) {
      const max = tooLong?.ctx?.max_length ?? Number((/(\d+)/.exec(tooLong?.msg || '') || [])[1]);
      return max
        ? this.i18n.t('Too long — use %{max} characters or fewer.', { max })
        : this.i18n.t('Too long — please shorten the name.');
    }
    switch (error?.status) {
      case 422: return this.i18n.t("That name can't be used. Try another.");
      case 404: return this.i18n.t('This conversation no longer exists.');
      case 0: return this.i18n.t("You're offline. Check your connection.");
      default: return this.i18n.t("Couldn't save the name. Try again.");
    }
  }

  /** Titles the learner has set, by session id. Local only — the API has nowhere to put them. */
  private storedTitles(): Record<string, string> {
    try {
      return JSON.parse(localStorage.getItem(TITLE_STORAGE_KEY) || '{}') || {};
    } catch {
      // Corrupt or unavailable storage must not stop the list rendering.
      return {};
    }
  }

  private storeTitle(sessionId: string, title: string): void {
    const titles = this.storedTitles();
    // An emptied field means "go back to the summarised title", not "show a blank row".
    if (title) { titles[sessionId] = title; } else { delete titles[sessionId]; }
    try {
      localStorage.setItem(TITLE_STORAGE_KEY, JSON.stringify(titles));
    } catch {
      // Not being able to remember the name is not worth failing the interaction over.
    }
  }

  private rowsFor(tab: FooziHistoryTab): any[] {
    return tab === 'chats' ? this.chats : tab === 'microActions' ? this.microActions : this.practice;
  }

  get rows(): any[] {
    return this.rowsFor(this.tab);
  }

  /** Whether to arm the infinite loader: only with rows already shown and more expected. */
  get canLoadMore(): boolean {
    return this.hasMore[this.tab] && !this.failed[this.tab] && this.rows.length > 0;
  }

  get isLoading(): boolean { return this.loading[this.tab] && !this.rows.length; }
  get hasFailed(): boolean { return this.failed[this.tab] && !this.rows.length; }
  get isEmpty(): boolean { return this.loaded[this.tab] && !this.failed[this.tab] && !this.rows.length; }

  /**
   * A chat replaces the conversation behind this modal, so it closes and the caller loads it.
   * The other two present their modal ON TOP from here — this one stays mounted underneath, which is
   * what keeps the tab, the scroll position and every page already fetched intact on the way back.
   */
  async openRow(row: any) {
    if (this.tab === 'chats') {
      // The conversation behind the modal. Handing it back as a selection would fetch and re-render
      // a transcript that is already on screen; closing is the whole of what was asked for.
      if (this.isActiveChat(row)) {
        this.modalController.dismiss(null, HISTORY_DISMISS_CURRENT);
        return;
      }
      this.modalController.dismiss({ tab: 'chats', row } as FooziHistorySelection);
      return;
    }
    await (this.tab === 'microActions' ? this.openMicroAction(row) : this.openPractice(row));
  }

  /** The micro-action, as its in-step screen. */
  private async openMicroAction(row: any): Promise<void> {
    const screenContentId = row?.screen_content_id ?? row?.screenContentId;
    if (!screenContentId) {
      this.toaster.error(this.i18n.t('That micro-action is no longer available.'));
      return;
    }
    const modal = await this.modalController.create({
      component: MicroActionHistoryModalComponent,
      componentProps: {
        screenContentId,
        customization: row,
        // Handed down from whatever opened this modal — the chips run its flows, not ours.
        onCommit: this.onCommit,
        onOpenTool: this.onOpenTool,
        onMarkCompleted: this.onMarkCompleted,
      },
      cssClass: this.modalCssClass,
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    // Only when something in there actually changed the row: customised, committed or completed.
    // Opening an action to read it and closing again is the common case, and it should cost nothing.
    if (data?.changed) { this.refreshTab('microActions', true); }
  }

  /**
   * The practice bot, opened on the report for this row's latest session. A history row is a record
   * of how the learner did, so the feedback is what they came for — the main screen, and every
   * earlier attempt, is one step back from there.
   */
  private async openPractice(row: any): Promise<void> {
    const screenContentId = row?.screen_content_id ?? row?.screenContentId;
    if (!screenContentId) {
      this.toaster.error(this.i18n.t('That practice session is no longer available.'));
      return;
    }
    const modal = await this.modalController.create({
      component: PracticeBotModalComponent,
      componentProps: {
        // No openSummarySessionId: the practice bot opens on its main screen. A row here stands for
        // the ACTION and everything practised against it, not for one attempt, so landing on the
        // latest report answered a narrower question than the one the row asks — and buried the
        // list of attempts, the scenario and the button to practise again one step behind a Back.
        // Title and wording both: the bot's scenario card shows the action, not just its name.
        action: {
          screenContentId,
          title: row?.title || '',
          description: row?.description || row?.text || '',
        },
      },
      cssClass: this.modalCssClass,
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    // A session practised in there, or a recording deleted, is a row here that has moved. Reading a
    // report and closing again is not.
    if (data?.changed) { this.refreshTab('practice', true); }
  }

  /**
   * Opens the action the caller asked for, once the page holding it has arrived.
   *
   * Cleared first: it is a one-shot instruction, and a later page must not open it a second time.
   * A row that never arrives simply leaves the learner on the list, which is where they were going
   * anyway.
   */
  private openRequestedAction(tab: FooziHistoryTab, staged?: any[]): void {
    // A quiet refresh is not the learner asking for anything; opening a modal out of one would be a
    // surprise. The request is honoured by the first real load.
    if (tab !== 'microActions' || this.openActionId == null || staged) { return; }
    const wanted = String(this.openActionId);
    const row = this.rowsFor(tab).find(
      (candidate: any) => String(candidate?.screen_content_id ?? candidate?.screenContentId) === wanted,
    );
    if (row) {
      this.openActionId = null;
      this.openMicroAction(row);
      return;
    }
    // Not on the pages fetched so far. The action that sent them here is from an earlier cycle,
    // which is exactly the kind that sits well down a list ordered by recency — so keep fetching
    // rather than leaving them to scroll for the row they just asked for.
    if (this.hasMore[tab] && this.requestedActionChases > 0) {
      this.requestedActionChases -= 1;
      this.load(tab);
      return;
    }
    // Out of reach. Dropped rather than left armed: a page fetched later by a scroll the learner
    // did themselves must not suddenly open a modal on them.
    this.openActionId = null;
  }

  private get modalCssClass(): string {
    return this.appState.get('isMobile')
      ? 'pratice-bot-custom-modal'
      : 'web-daily-steps-custom-modal web-pratice-bot-custom-modal';
  }

  close() {
    this.modalController.dismiss(null, 'closed');
  }

  // ── Deleting history ─────────────────────────────────────────────────────────
  // Neither of these is wired to a button at the moment: the Chats bin was dropped as unnecessary
  // for now, and practice deletion was dropped to avoid the complexity. Both flows are kept
  // deliberately — the endpoints exist and the requirement is expected back.

  /**
   * Deletes one conversation. Confirmed first: it is permanent, and a bin sits inside a row whose
   * whole job is to be tapped.
   */
  async deleteChat(row: any, event: Event) {
    event.stopPropagation();
    const sessionId = row?.session_id;
    if (!sessionId) { return; }

    this.toaster.showConfirm(
      this.i18n.t('Delete this conversation? This cannot be undone.'),
      () => {
        this.fooziService.deleteSession(sessionId).subscribe(
          () => {
            this.toaster.success(this.i18n.t('Conversation deleted'));
            this.refreshTab('chats');
          },
          () => this.toaster.error(this.i18n.t('We could not delete that conversation.')),
        );
      },
    );
  }

  /**
   * Deletes every practice session recorded against one micro-action — what the row represents.
   * Confirmed first: it is not recoverable, and the row is one tap from the one that opens it.
   */
  async deletePractice(row: any, event: Event) {
    event.stopPropagation();          // the row itself opens the action; the bin must not
    const screenContentId = row?.screenContentId;
    if (!screenContentId) { return; }

    this.toaster.showConfirm(
      this.i18n.t('Delete every practice session for this micro-action? This cannot be undone.'),
      () => {
        this.practiceBotService.deletePracticeSessionsForAction(screenContentId).subscribe(
          () => {
            this.toaster.success(this.i18n.t('Practice history deleted'));
            this.refreshTab('practice');
          },
          () => this.toaster.error(this.i18n.t('We could not delete that practice history.')),
        );
      },
    );
  }

  /** "12 messages · 27 Aug" — whatever the row actually carries, never a blank line. */
  rowSubtitle(row: any): string {
    if (this.tab === 'chats') {
      const count = row?.message_count ?? 0;
      const label = count === 1 ? this.i18n.t('message') : this.i18n.t('messages');
      // Named as a stream: /sessions returns chat sessions only now, and saying so distinguishes
      // these rows from the micro-action threads on the next tab.
      return `${this.i18n.t('Coach chat')} · ${count} ${label}`;
    }
    if (this.tab === 'practice') {
      // Attempts that produced a report, not every attempt: an abandoned or still-analyzing one is
      // not a session the learner can look back at. The confidence progression is deliberately not
      // here — the chip beside it carries the score.
      const sessions = row?.completedCount ?? 0;
      const label = sessions === 1 ? this.i18n.t('session') : this.i18n.t('sessions');
      return `${sessions} ${label}`;
    }
    // Micro-actions carry their own chip-and-quote line instead of a subtitle.
    return '';
  }

  /**
   * Where a customised micro-action stands, in three states rather than two:
   *
   *   Completed     green   — the learner has done it
   *   Committed     amber   — scheduled, not yet done
   *   Not committed red     — customised and left there (this was "Draft")
   */
  actionStatus(row: any): 'completed' | 'committed' | 'not-committed' {
    // Mapped to this modal's three tokens, never passed through. The value goes into a class name
    // ('fh-row__chip--' + this), and the server's own vocabulary is snake_case: `not_committed`
    // reached the template as fh-row__chip--not_committed, which matches no rule, so the chip read
    // "Not committed" in an unstyled badge rather than in red. A row carrying only the booleans
    // yielded `undefined` and did the same.
    const status = String(row?.action_status ?? '').toLowerCase().replace(/_/g, '-');
    if (status === 'completed' || status === 'committed') { return status; }
    if (status === 'not-committed') { return 'not-committed'; }

    // No status field: older rows say it with a pair of booleans instead.
    if (row?.is_completed ?? row?.user_action_completion) { return 'completed'; }
    return row?.is_committed ? 'committed' : 'not-committed';
  }

  /**
   * The cycle a row belongs to.
   *
   * Named on every row that has one, current cycle included: History spans them all, and a learner
   * scanning the list should be able to place a row without working out which ones are unlabelled.
   * Which cycle is the current one still decides the ORDER — see orderByCycle — just not the label.
   */
  cycleLabel(row: any): string {
    return (row?.cycle_name || row?.cycleName || '').trim();
  }

  /**
   * Whether a row belongs to a cycle other than the one the learner is in.
   *
   * A row with no cycle on it counts as the current one: rows from before the field was returned
   * must not all sort to the bottom as though they were someone else's.
   */
  private isOtherCycle(row: any): boolean {
    const rowCycleId = row?.cycle_id ?? row?.cycleId;
    if (rowCycleId == null) { return false; }
    const currentCycleId = this.appState.get('currentPath')?.id;
    if (currentCycleId == null) { return true; }
    return String(rowCycleId) !== String(currentCycleId);
  }

  /**
   * Brings the current cycle's rows to the top, leaving everything else below them.
   *
   * What the learner is working on now is what they came for; an action from a cycle they finished
   * months ago is a look-back. The sort is stable, so within each group the server's own ordering —
   * newest first — is untouched, and it runs on the whole list after every page so a later page's
   * current-cycle row joins the top group rather than trailing the ones already on screen.
   */
  private orderByCycle(tab: FooziHistoryTab): void {
    this.rowsFor(tab).sort(
      (a: any, b: any) => Number(this.isOtherCycle(a)) - Number(this.isOtherCycle(b)),
    );
  }

  /** "v9 · Committed" — which revision this is, and where it stands. */
  versionLabel(row: any): string {
    const status = this.actionStatus(row);
    const state = status === 'completed' ? this.i18n.t('Completed')
      : status === 'committed' ? this.i18n.t('Committed')
      : this.i18n.t('Not committed');
    const version = row?.version;
    return version == null ? state : `v${version} · ${state}`;
  }

  /** The wording that revision produced. */
  rowWording(row: any): string {
    return (row?.description || row?.text || '').trim();
  }

  /**
   * Whether this row is the conversation open in the panel behind the modal.
   *
   * Chats only: the other two tabs list actions and practice, neither of which the panel can be
   * "in". A row the learner is already reading is worth marking rather than leaving them to open it
   * and find they were there already.
   */
  isActiveChat(row: any): boolean {
    if (this.tab !== 'chats' || !this.activeSessionId) { return false; }
    return String(row?.session_id) === String(this.activeSessionId);
  }

  rowTitle(row: any): string {
    if (this.tab === 'chats') { return this.chatTitle(row); }
    if (this.tab === 'practice') { return row?.title || this.i18n.t('Practice session'); }

    return row?.title || row?.action_title || this.i18n.t('Micro-action');
  }

  /** Today / Mon / 28 Jun — short enough for the right-hand column at any width. */
  /**
   * The latest confidence score, which now stands in for the ready/practise-again wording — a
   * number the learner can compare against their last one says more than a verdict.
   *
   * Null when nothing has completed: an action can have attempts and still no score (abandoned, or
   * still analyzing), and "0" would read as a bad result rather than as no result.
   */
  /**
   * Whether a conversation holds micro-action turns the Micro-actions screen can no longer show.
   *
   * Both conditions have to hold. The date alone would flag every old conversation, most of which
   * never customised anything; the record alone is only learned once a conversation has been
   * opened. Together they say it only where it is true.
   *
   * The cutover date is per environment — internal testing gets the split before the public date,
   * so no single constant is right for both.
   */
  mayHoldMicroActionTurns(row: any): boolean {
    if (this.tab !== 'chats') { return false; }

    // If the server says outright, that settles it — it can see the turns. Explicit false is an
    // answer too, so the check is for the field being present rather than truthy.
    if (typeof row?.may_hold_micro_action_turns === 'boolean') {
      return row.may_hold_micro_action_turns;
    }

    // Otherwise: started before the cutover, so it COULD hold both kinds of turn. Deliberately not
    // narrowed further — knowing for certain means reading each conversation's transcript, which
    // costs a request per row to soften the wording of a note.
    const splitAt = this.streamSplitAt();
    if (splitAt === null) { return false; }
    const started = Date.parse(row?.created_at || row?.updated_at || '');
    return isNaN(started) ? false : started < splitAt;
  }

  /**
   * The configured cutover as a timestamp, or null when there is not a readable one.
   *
   * Tolerant of the shorthand a hand-edited config tends to carry — `2026-09-1`, `2026-9-1` —
   * because Date.parse rejects a single-digit day outright, which switched the note off everywhere
   * with nothing to say why.
   */
  private streamSplitAt(): number | null {
    const configured = (environment.streamSplitAt || '').trim();
    if (!configured) { return null; }

    const direct = Date.parse(configured);
    if (!isNaN(direct)) { return direct; }

    const loose = configured.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (!loose) { return null; }
    const [, year, month, day] = loose;
    const padded = Date.parse(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00Z`);
    return isNaN(padded) ? null : padded;
  }

  latestScore(row: any): number | null {
    const scores: number[] = row?.scores || [];
    return scores.length ? scores[scores.length - 1] : null;
  }

  /**
   * Green above the target score, amber below it. Read off the number rather than the server's
   * ready/practise-again verdict, so the colour and the figure beside it can never disagree.
   */
  scoreTone(row: any): 'ready' | 'practicing' {
    const score = this.latestScore(row);
    return score != null && score > PRACTICE_SCORE_TARGET ? 'ready' : 'practicing';
  }

  /** "72/100" on the first row, then bare numbers read against that scale. */
  scoreLabel(row: any, index: number): string {
    const score = this.latestScore(row);
    if (score == null) { return ''; }
    return index === 0 ? `${score}/100` : `${score}`;
  }

  rowWhen(row: any): string {
    const raw = row?.latestAt || row?.updated_at || row?.created_at || row?.at || row?.ended_at;
    // The learner's language, not the browser's: a Portuguese screen was labelling its rows "Thu".
    return historyDateLabel(raw, this.i18n.t('Today'), this.i18n.locale);
  }

  trackRow(_index: number, row: any) {
    return row?.session_id ?? row?.customization_id ?? row?.screenContentId ?? row?.practice_session_id ?? row?.id ?? _index;
  }
}
